/**
 * 應用入口頁面
 * LIFF 初始化、登入檢查和頁面導向
 * 這是 LINE Developers Console 中設定的 Endpoint URL 目標頁面
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { lineLogin, getStoredAccessToken, getMyDriverInfo, clearAuthData } from '@/services/driverAuthService'
import { Box, CircularProgress, Typography, Button, Paper } from '@mui/material'
import LoginIcon from '@mui/icons-material/Login'
import ErrorIcon from '@mui/icons-material/Error'

const EntryPage: React.FC = () => {
  const navigate = useNavigate()
  const { isLoggedIn, loading, error, login, getIdToken } = useLiff()
  const [initError, setInitError] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_checkingBinding, setCheckingBinding] = useState(false)

  // Debug: 確認環境變數
  console.log('EntryPage - VITE_LIFF_ID:', import.meta.env.VITE_LIFF_ID)

  // ========== LIFF 初始化完成後的處理 ==========
  useEffect(() => {
    const checkBindingStatus = async () => {
      if (!loading && !error && isLoggedIn) {
        // 已登入 - 檢查是否已綁定司機帳號
        setCheckingBinding(true)

        try {
          // 快速路徑：localStorage 有未過期的 accessToken → 先向後端確認它真的能用，再導首頁。
          // 不能只看 client 端的 exp：token 可能簽章對不上（bundle 曾指到別的環境、後端換過
          // JWT_SECRET）或被列入黑名單，直接跳首頁會在 403 之間打轉（2026-09-14 對外 LIFF 事故）。
          // drivers/me 失敗時 apiClient 的 403 處理會用 LINE ID Token 重新登入並自行導頁；
          // 走到 catch 代表連重新登入都沒救回來，清掉舊 token 照正常流程重跑一次 lineLogin。
          const existingToken = getStoredAccessToken()
          if (existingToken) {
            try {
              const me = await getMyDriverInfo()
              if (me) {
                console.log('accessToken 經後端確認有效，導向首頁')
                navigate('/home', { replace: true })
                return
              }
              console.log('accessToken 有效但尚未綁定司機，導向綁定頁面')
              navigate('/binding', { replace: true })
              return
            } catch (err) {
              console.warn('既有 accessToken 無法使用，清除後重新登入:', err)
              clearAuthData()
            }
          }

          // 呼叫後端 LINE 登入 API
          const lineIdToken = getIdToken()
          if (lineIdToken) {
            console.log('正在進行 LINE 登入...')
            const loginResult = await lineLogin(lineIdToken)

            if (loginResult.success) {
              // 登入成功 - 更新 localStorage 並導向首頁
              console.log('LINE 登入成功，更新 localStorage 並導向首頁')
              localStorage.setItem(
                'driver-binding',
                JSON.stringify({
                  driverId: loginResult.driverId,
                  driverName: loginResult.driverName,
                  companyCode: loginResult.companyCode,
                  companyName: loginResult.companyName,
                  bindingDate: new Date().toISOString(),
                })
              )
              navigate('/home', { replace: true })
            } else {
              // 登入失敗（未綁定）- 導向綁定頁面
              console.log('LINE 登入失敗（未綁定），導向綁定頁面:', loginResult.message)
              navigate('/binding', { replace: true })
            }
          } else {
            // 無法取得 ID Token - 導向綁定頁面
            console.log('無法取得 LINE ID Token，導向綁定頁面')
            navigate('/binding', { replace: true })
          }
        } catch (err) {
          console.error('LINE 登入失敗:', err)
          // API 錯誤時導向綁定頁面（讓使用者嘗試重新綁定）
          navigate('/binding', { replace: true })
        } finally {
          setCheckingBinding(false)
        }
      } else if (!loading && !error && !isLoggedIn) {
        // 未登入 - 停留在此頁面顯示登入按鈕
        console.log('LIFF 未登入，等待使用者操作')
      }

      if (error) {
        setInitError(error)
      }
    }

    checkBindingStatus()
  }, [loading, error, isLoggedIn, navigate, getIdToken])

  // ========== 處理登入 ==========
  const handleLogin = async () => {
    try {
      await login()
    } catch (err) {
      console.error('登入失敗:', err)
      setInitError(err instanceof Error ? err.message : '登入失敗')
    }
  }

  // ========== 渲染：載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="h6" color="text.secondary">
          正在初始化 LIFF...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          請稍候
        </Typography>
      </Box>
    )
  }

  // ========== 渲染：錯誤狀態 ==========
  if (error || initError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <ErrorIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            初始化失敗
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error || initError}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              可能原因：
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              • LIFF ID 設定錯誤
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              • Endpoint URL 設定錯誤
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              • 需要在 LINE App 中開啟
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={() => window.location.reload()}
          >
            重新載入
          </Button>
        </Paper>
      </Box>
    )
  }

  // ========== 渲染：未登入狀態（顯示登入按鈕） ==========
  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#6B705C',
                mb: 1,
              }}
            >
              好運網
            </Typography>
            <Typography variant="body2" color="text.secondary">
              LINE LIFF 應用
            </Typography>
          </Box>

          {/* 說明 */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            請使用 LINE 帳號登入以繼續使用
          </Typography>

          {/* 登入按鈕 */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            sx={{
              backgroundColor: '#06C755', // LINE Green
              color: 'white',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#05B34B',
              },
            }}
          >
            使用 LINE 登入
          </Button>

          {/* 注意事項 */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, display: 'block' }}
          >
            登入即表示您同意我們的服務條款與隱私權政策
          </Typography>

          {/* LIFF ID - 低調顯示 */}
          <Typography
            variant="caption"
            sx={{
              mt: 1.5,
              display: 'block',
              fontSize: '0.75rem',
              color: '#757575',
              textAlign: 'center',
            }}
          >
            LIFF: {import.meta.env.VITE_LIFF_ID || '-'}
          </Typography>
        </Paper>
      </Box>
    )
  }

  // ========== 渲染：已登入（通常不會顯示，因為會自動導向） ==========
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        gap: 2,
      }}
    >
      <CircularProgress size={60} sx={{ color: '#6B705C' }} />
      <Typography variant="h6" color="text.secondary">
        登入成功！正在導向...
      </Typography>
    </Box>
  )
}

export default EntryPage
